import React, { FC } from "react";
import { Alert, Card } from "react-bootstrap";

import { DayDetail, WeekList } from "../../../../components";
import { PlanDayType, PlanWeekType } from "../../../../DTO";

type SelectorPanelProps = {
    activeTab: "weekly" | "daily";
    setActiveTab: (tab: "weekly" | "daily") => void;
    userId: number;
    hasWeekSelection: boolean;
    selectedWeek?: PlanWeekType | null;
    currentDay: string;
    setCurrentDay: React.Dispatch<React.SetStateAction<string>>;
    onWeekSelect: (week: PlanWeekType) => void;
    onDaySelect: (day: PlanDayType) => void;
};

export const SelectorPanel: FC<SelectorPanelProps> = ({
    activeTab,
    setActiveTab,
    userId,
    hasWeekSelection,
    selectedWeek,
    currentDay,
    setCurrentDay,
    onWeekSelect,
    onDaySelect,
}) => {
    const selectedWeekLabel =
        selectedWeek && (selectedWeek.index ?? 0) > 0 ? `Week ${selectedWeek.index}` : "—";
    const selectedDayLabel = currentDay || "—";

    return (
        <Card className="summary-panel summary-panel-compact shadow-sm border-0">
            <Card.Body className="p-2">
                <div className="summary-tabs summary-tabs-compact mb-2" role="tablist" aria-label="Summary tabs">
                    <button
                        type="button"
                        className={`summary-tab-btn ${activeTab === "weekly" ? "summary-tab-btn-active" : ""}`}
                        onClick={() => setActiveTab("weekly")}
                        aria-selected={activeTab === "weekly"}
                        role="tab"
                    >
                        Weekly
                    </button>
                    <button
                        type="button"
                        className={`summary-tab-btn ${activeTab === "daily" ? "summary-tab-btn-active" : ""}`}
                        onClick={() => setActiveTab("daily")}
                        aria-selected={activeTab === "daily"}
                        role="tab"
                    >
                        Daily
                    </button>
                </div>

                <div className="summary-selector-status mb-2">
                    <span className="summary-status-chip">
                        Week: {selectedWeekLabel}
                    </span>
                    {activeTab === "daily" && (
                        <span className="summary-status-chip">
                            Day: {selectedDayLabel}
                        </span>
                    )}
                </div>

                <div className="d-flex flex-column gap-2">
                    <div className="summary-selection-wrap summary-selection-wrap-compact">
                        <div className="summary-selection-head summary-selection-head-compact">
                            <div className="summary-selection-title-wrap">
                                <span className="summary-selection-icon" aria-hidden="true">W</span>
                                <h3 className="summary-selector-title mb-0">Week</h3>
                            </div>
                            <span className="summary-step-badge">Step 1</span>
                        </div>
                        <WeekList pageId={userId} onWeekSelect={onWeekSelect} variant="grid" />
                    </div>

                    {activeTab === "daily" && (
                        <div className="summary-selection-wrap summary-selection-wrap-compact">
                            <div className="summary-selection-head summary-selection-head-compact">
                                <div className="summary-selection-title-wrap">
                                    <span className="summary-selection-icon" aria-hidden="true">D</span>
                                    <h3 className="summary-selector-title mb-0">Day</h3>
                                </div>
                                <span className="summary-step-badge">Step 2</span>
                            </div>
                            {hasWeekSelection && (selectedWeek?.id ?? 0) > 0 ? (
                                <DayDetail
                                    userId={userId}
                                    currentDay={currentDay}
                                    setCurrentDay={setCurrentDay}
                                    autoSelectFirstDay={false}
                                    autoSelectCurrentDay={true}
                                    onDaySelect={onDaySelect}
                                    variant="compact"
                                />
                            ) : (
                                <Alert variant="light" className="mb-0 py-2 small">
                                    Select a week first.
                                </Alert>
                            )}
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};
